import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/useAuthStore'

interface RequireAuthZProps {
  children: React.ReactNode
}

/**
 * Z-specific auth wrapper that redirects to /z/login instead of /login.
 * Also checks approval status and redirects to pending page if not approved.
 * After approval, checks email verification status.
 * Keeps users within the Z experience.
 */
export function RequireAuthZ({ children }: RequireAuthZProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, loading, initialized, initialize, approvalStatus, approvalChecked, emailVerified } = useAuthStore()

  // Initialize auth on mount
  useEffect(() => {
    initialize()
  }, [initialize])

  // Redirect to Z login if not authenticated
  useEffect(() => {
    if (initialized && !loading && !user) {
      const returnTo = encodeURIComponent(location.pathname)
      navigate(`/z/login?returnTo=${returnTo}`, { replace: true })
    }
  }, [initialized, loading, user, navigate, location.pathname])

  // Redirect to pending approval page if not approved
  useEffect(() => {
    if (initialized && user && approvalChecked && approvalStatus !== 'approved') {
      navigate('/z/pending', { replace: true })
    }
  }, [initialized, user, approvalChecked, approvalStatus, navigate])

  // Redirect to verification pending page if approved but email not verified
  useEffect(() => {
    if (initialized && user && approvalChecked && approvalStatus === 'approved' && !emailVerified) {
      navigate('/z/verify-pending', { replace: true })
    }
  }, [initialized, user, approvalChecked, approvalStatus, emailVerified, navigate])

  // Loading state
  if (!initialized || loading || (user && !approvalChecked)) {
    return (
      <div className="h-dvh flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-6 h-6 border border-foreground/20 border-t-foreground rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-sm text-muted-foreground font-light">
            Loading...
          </p>
        </div>
      </div>
    )
  }

  // Not authenticated
  if (!user) {
    return null
  }

  // Not approved
  if (approvalStatus !== 'approved') {
    return null
  }

  // Approved but email not verified
  if (!emailVerified) {
    return null
  }

  return <>{children}</>
}
